// Send later on a decision card (U12): the "When it goes" choice in the armed send strip, the
// "Sends tomorrow at 8:30 · Change · Cancel" line while a scheduled send waits, and the calm note
// on a card whose scheduled send came back because someone wrote first.
import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { CalendarClock } from "lucide-react";
import { toast } from "sonner";
import type { CardView, WorkView } from "@shared/api";
import { formatFull, formatShortDateTime } from "@web/lib/time";
import { useNow } from "@web/lib/useNow";
import { Button } from "@web/ui/Button";
import { SoftBanner } from "@web/ui/SoftBanner";
import { cancelWork } from "@web/feed/cardOps";
import { invalidateScheduled, useSendZone } from "./sendLaterApi";
import { sendLaterStore, useCardSendAt } from "./sendLaterStore";
import { SendLaterPicker, useSendLabel } from "./SendLaterPicker";
import { capitalize, sendAtWords } from "./when";

/** The strip's "When it goes": the card's choice lives in sendLaterStore until it is confirmed. */
export function CardSendLater({
  cardId,
  disabled,
  reply,
}: {
  cardId: string;
  disabled: boolean;
  reply: boolean;
}) {
  const value = useCardSendAt(cardId);
  // "Change" on a scheduled send: open on the time it had.
  const [startAt] = useState(() => sendLaterStore.peekChange(cardId));
  useEffect(() => {
    sendLaterStore.takeChange(cardId);
    if (startAt) sendLaterStore.set(cardId, startAt);
    return () => sendLaterStore.clear(cardId);
  }, [cardId, startAt]);
  return (
    <SendLaterPicker
      value={value}
      onChange={(next) => sendLaterStore.set(cardId, next)}
      disabled={disabled}
      reply={reply}
    />
  );
}

/** The confirm button's words and whether it can be pressed, for the card's send choice. */
export function useCardSendLabel(cardId: string, enabled: boolean, label: string) {
  const value = useCardSendAt(cardId);
  return useSendLabel(enabled ? value : null, label);
}

/** Under a queued card: when its executor work runs; for a scheduled send, Change and Cancel. */
export function QueuedRunNote({ card, work }: { card: CardView; work: WorkView }) {
  const queryClient = useQueryClient();
  const zone = useSendZone();
  const now = useNow(60_000);
  const [busy, setBusy] = useState(false);
  if (!work.notBefore) return null;
  const at = work.scheduledFor;
  if (!at)
    return (
      <p className="text-sm text-muted" title={formatFull(work.notBefore)}>
        Runs after {formatShortDateTime(work.notBefore)}. You can still cancel.
      </p>
    );
  const cancel = async (change: boolean) => {
    setBusy(true);
    try {
      await cancelWork(queryClient, work.id);
      invalidateScheduled(queryClient);
      if (change) sendLaterStore.requestChange(card.id, at);
      toast(
        change
          ? "Back in review with your draft. Send it again to pick another time."
          : "Cancelled. Nothing will be sent; the card is back for review.",
      );
    } catch {
      toast("Couldn't change it just now. It's still scheduled.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
      <span className="inline-flex items-center gap-1.5 text-ink" title={formatFull(at)}>
        <CalendarClock aria-hidden className="size-4 text-muted" />
        Sends {sendAtWords(at, now, zone)}
      </span>
      <span aria-hidden className="text-faint">
        ·
      </span>
      <Button size="sm" variant="ghost" disabled={busy} onClick={() => void cancel(true)}>
        Change
      </Button>
      <span aria-hidden className="text-faint">
        ·
      </span>
      <Button
        size="sm"
        variant="ghost"
        disabled={busy}
        onClick={() => void cancel(false)}
        data-testid="scheduled-cancel"
      >
        Cancel
      </Button>
    </div>
  );
}

/** A card whose scheduled send came back: who wrote, so the owner looks before it goes. */
export function ScheduledReturnNote({ card }: { card: CardView }) {
  const last = card.history.at(-1);
  if (last?.type !== "send_later.returned" || !last.detail) return null;
  if (card.status !== "to_review_updated") return null;
  return <SoftBanner>{capitalize(last.detail)}</SoftBanner>;
}
